import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import ClientDetails from '../components/file-opening/ClientDetails';
import Address from '../components/file-opening/Address';
import Identification from '../components/file-opening/Identification';
import SponsorDetails from '../components/file-opening/SponsorDetails';
import LawyerCosts from '../components/file-opening/LawyerCosts';
import CaseInfo from '../components/file-opening/CaseInfo';
import Input from '../components/elements/Input';
import ProceedingDetails from '../components/file-opening/ProceedingDetails';
import { useAuth } from '../contexts/AuthContext';
import { createFileOpening, getFileOpeningById, updateFileOpening } from '../services/fileOpenings';
import toast from 'react-hot-toast';
import { initData } from '../data/fileOpening';
import FileOpeningPdf from '../components/pdf/FileOpeningPdf';
import { pdf } from '@react-pdf/renderer';
import FixedFeePdf from '../components/pdf/FixedFeePdf';
import ClientCareGeneralPdf from '../components/pdf/ClientCareGeneralPdf';

const FileOpeningCreate = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState(() => {
    const savedData = localStorage.getItem('fileOpeningFormData');
    return id ? initData : (savedData ? JSON.parse(savedData) : initData);
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) {
      localStorage.setItem('fileOpeningFormData', JSON.stringify(formData));
    }
  }, [formData, id]);

  useEffect(() => {
    if (id) {
      const fetchFileOpening = async () => {
        try {
          const data = await getFileOpeningById(id);
          setFormData(data);
        } catch (error) {
          toast.error(error.message);
        } finally {
          setLoading(false);
        }
      };
      fetchFileOpening();
    } else {
      setLoading(false);
    }
  }, [id]);

  const resetForm = (e) => {
    e.preventDefault();
    setFormData(initData);
    localStorage.removeItem('fileOpeningFormData');
    setStep(1);
    return;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const downloadPdf = async (data) => {
    try {
      const blob = await pdf(<FileOpeningPdf data={data} />).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `fileOpening-${data.id || 'new'}-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF');
    }
    try {
      const blob = await pdf(<FixedFeePdf data={data} />).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `FixedFee-${data.id || 'new'}-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating Fee PDF:', error);
      toast.error('Failed to generate Fee PDF');
    }
    try {
      const blob = await pdf(<ClientCareGeneralPdf data={data} />).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ClientCare-${data.id || 'new'}-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating Client Care PDF:', error);
      toast.error('Failed to generate Client Care PDF');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const dataToSubmit = {
      ...formData,
      user_id: user?.id,
      fixed_fee: formData.fixed_fee === '' ? null : formData.fixed_fee,
      hourly_rate: formData.hourly_rate === '' ? null : formData.hourly_rate,
      date: formData.date === '' ? null : formData.date,
      dob: formData.dob === '' ? null : formData.dob,
      key_date: formData.key_date === '' ? null : formData.key_date,
    };

    try {
      if (id) {
        await updateFileOpening(id, dataToSubmit);
        await downloadPdf(dataToSubmit);
        toast.success('File opening updated successfully!');
        navigate(`/file-opening/${id}`);
      } else {
        const data = await createFileOpening(dataToSubmit);
        await downloadPdf(data);
        toast.success('File opening created successfully!');
        localStorage.removeItem('fileOpeningFormData');
        navigate('/file-openings');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return <CaseInfo formData={formData} handleChange={handleChange} />;
      case 2:
        return <ClientDetails formData={formData} handleChange={handleChange} />;
      case 3:
        return <ProceedingDetails formData={formData} handleChange={handleChange} />;
      case 4:
        return <Address formData={formData} handleChange={handleChange} />;
      case 5:
        return <Identification formData={formData} handleChange={handleChange} />;
      case 6:
        return <SponsorDetails formData={formData} handleChange={handleChange} />;
      case 7:
        return <LawyerCosts formData={formData} handleChange={handleChange} />;
      case 8:
        return (
          <div>
            <div className="mb-6">
              <Input label="Completed By" type="text" name="completed_by" value={formData.completed_by} onChange={handleChange} />
            </div>
            <div className="mb-6">
              <label className="block font-semibold mb-1 text-gray-800 dark:text-white">Matter Description</label>
              <textarea name="matter_description" className="w-full border rounded-md p-2 bg-white dark:bg-gray-700 text-gray-800 dark:text-white border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400" rows="3" placeholder="Enter matter description..." value={formData.matter_description} onChange={handleChange}></textarea>
            </div>
          </div>
        );
      default:
        return <CaseInfo formData={formData} handleChange={handleChange} />;
    }
  };

  if (loading) {
    return <DashboardLayout pageTitle="Loading..."><p className='dark:text-white'>Loading...</p></DashboardLayout>;
  }

  return (
    <DashboardLayout pageTitle={id ? 'Edit File Opening' : 'New File Opening'}>
      <div className="max-w-4xl mx-auto">
        <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-4 sm:p-8 my-10">
          <div className="flex justify-center items-center mb-6 relative">
            <h1 className="text-xl sm:text-2xl font-bold text-center text-gray-800 dark:text-white">File Opening & Ledger Request Sheet</h1>
            <button className='text-red-600 hover:text-red-800 absolute right-0' onClick={(e) => resetForm(e)}>Reset Form</button>
            {id && (
              <button type="button" onClick={() => navigate(`/file-opening/${id}`)} className="text-red-600 absolute right-0 top-8 hover:text-red-800 font-semibold text-sm sm:text-base">
                Cancel
              </button>
            )}
          </div>
          <p className="text-xs sm:text-sm text-center mb-8 text-gray-500 dark:text-gray-400">Fee Earner's Responsibility – Please complete all required fields</p>

          {renderStep()}

          <div className="flex justify-between mt-8">
            {step > 1 && (
              <button type="button" onClick={() => setStep(step - 1)} className="bg-gray-600 hover:bg-gray-700 text-white font-semibold px-4 sm:px-6 py-2 rounded-md shadow text-sm sm:text-base">
                Previous
              </button>
            )}
            <div />
            {step < 8 && (
              <button type="button" onClick={() => setStep(step + 1)} className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 sm:px-6 py-2 rounded-md shadow text-sm sm:text-base">
                Next
              </button>
            )}
            {step === 8 && (
              <button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 sm:px-6 py-2 rounded-md shadow text-sm sm:text-base">
                {id ? 'Save Changes' : 'Submit Form'}
              </button>
            )}
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
};

export default FileOpeningCreate;
